import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  ShoppingCart, Send, RefreshCw, Loader2, Search, Mail,
  Clock, DollarSign, Users, Calendar, AlertCircle, CheckCircle,
  Phone, MapPin, Trash2, Settings
} from 'lucide-react';

export default function AbandonedCartsTab() {
  const [loading, setLoading] = useState(true);
  const [carts, setCarts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settings, setSettings] = useState({
    auto_send_enabled: true,
    auto_send_delay_hours: 24,
    reminder_email_subject: "You left something behind!",
    discount_code: "",
    discount_percent: 10
  });

  useEffect(() => {
    loadCarts();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/admin/abandoned-cart-settings');
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/abandoned-cart-settings', settings);
      toast.success('Settings saved!');
      setShowSettingsDialog(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const loadCarts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/abandoned-carts');
      setCarts(res.data || []);
    } catch (error) {
      console.error('Error loading abandoned carts:', error);
      toast.error('Failed to load abandoned carts');
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (cartId) => {
    setSendingReminder(cartId);
    try {
      await api.post(`/admin/abandoned-carts/${cartId}/send-reminder`);
      toast.success('Reminder sent successfully!');
      loadCarts();
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const filteredCarts = carts.filter(cart =>
    cart.tour_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cart.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cart.phone?.includes(searchQuery)
  );

  // Stats
  const totalValue = carts.reduce((sum, c) => sum + (c.total_price || 0), 0);
  const withEmail = carts.filter(c => c.email).length;
  const reminderSent = carts.filter(c => c.reminder_sent).length;

  // Time since abandoned
  const getTimeSince = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const now = new Date();
    const hours = Math.floor((now - date) / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="abandoned-carts-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="h-6 w-6 text-orange-500" />
            Abandoned Cart Recovery
          </h2>
          <p className="text-muted-foreground">Recover lost sales by sending reminders</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadCarts} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => setShowSettingsDialog(true)} variant="outline" className="gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
              <ShoppingCart className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Abandoned Carts</p>
              <p className="text-xl font-bold text-orange-600">{carts.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Potential Revenue</p>
              <p className="text-xl font-bold text-red-600">${totalValue.toFixed(2)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Mail className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">With Email</p>
              <p className="text-xl font-bold text-blue-600">{withEmail}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Reminders Sent</p>
              <p className="text-xl font-bold text-green-600">{reminderSent}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by tour, email, or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Carts List */}
      <Card className="p-6">
        {filteredCarts.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-lg font-medium text-muted-foreground">No abandoned carts</p>
            <p className="text-sm text-muted-foreground">Great news! No carts have been abandoned recently.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCarts.map((cart) => (
              <div 
                key={cart.id} 
                className={`p-4 border rounded-lg ${cart.reminder_sent ? 'bg-green-50/50 border-green-200' : 'bg-orange-50/50 border-orange-200'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{cart.tour_title || 'Unknown Tour'}</h3>
                      <Badge variant={cart.reminder_sent ? 'default' : 'secondary'}>
                        {cart.reminder_sent ? 'Reminder Sent' : 'Pending'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>{cart.guest_count || 1} guests</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>{cart.selected_date || 'No date'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{getTimeSince(cart.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2 font-semibold text-orange-600">
                        <DollarSign className="h-4 w-4" />
                        <span>${cart.total_price?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                    
                    {(cart.email || cart.phone) && (
                      <div className="mt-3 flex items-center gap-4 text-sm">
                        {cart.email && (
                          <div className="flex items-center gap-1 text-blue-600">
                            <Mail className="h-3 w-3" />
                            <span>{cart.email}</span>
                          </div>
                        )}
                        {cart.phone && (
                          <div className="flex items-center gap-1 text-green-600">
                            <Phone className="h-3 w-3" />
                            <span>{cart.phone}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    {cart.email && !cart.reminder_sent && (
                      <Button 
                        size="sm" 
                        onClick={() => sendReminder(cart.id)}
                        disabled={sendingReminder === cart.id}
                        className="gap-2"
                      >
                        {sendingReminder === cart.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        Send Reminder
                      </Button>
                    )}
                    {cart.reminder_sent && (
                      <Badge variant="outline" className="gap-1 bg-green-100">
                        <CheckCircle className="h-3 w-3" />
                        Sent
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Tips */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          Recovery Tips
        </h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Send reminders within 24 hours for best results</li>
          <li>• Carts with email addresses have 3x higher recovery rate</li>
          <li>• Consider offering a small discount in the reminder email</li>
          <li>• Follow up with a second reminder after 48 hours if no response</li>
        </ul>
      </Card>

      {/* Auto-Send Status Banner */}
      <Card className={`p-4 ${settings.auto_send_enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${settings.auto_send_enabled ? 'bg-green-500' : 'bg-gray-400'}`}>
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h4 className="font-medium">{settings.auto_send_enabled ? 'Auto-Send Enabled' : 'Auto-Send Disabled'}</h4>
              <p className="text-sm text-muted-foreground">
                {settings.auto_send_enabled 
                  ? `Recovery emails will be sent automatically after ${settings.auto_send_delay_hours} hours`
                  : 'Emails must be sent manually'}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowSettingsDialog(true)}>
            Configure
          </Button>
        </div>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Abandoned Cart Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Send Recovery Emails</Label>
                <p className="text-xs text-muted-foreground">Automatically send reminders to abandoned carts</p>
              </div>
              <Switch
                checked={settings.auto_send_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, auto_send_enabled: checked })}
              />
            </div>
            
            {settings.auto_send_enabled && (
              <>
                <div>
                  <Label>Send After (Hours)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      min="1"
                      max="72"
                      value={settings.auto_send_delay_hours}
                      onChange={(e) => setSettings({ ...settings, auto_send_delay_hours: parseInt(e.target.value) || 24 })}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">hours after cart is abandoned</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Recommended: 24 hours for best recovery rate
                  </p>
                </div>
                
                <div>
                  <Label>Email Subject</Label>
                  <Input
                    value={settings.reminder_email_subject}
                    onChange={(e) => setSettings({ ...settings, reminder_email_subject: e.target.value })}
                    placeholder="You left something behind!"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Include Discount Code (Optional)</Label>
                  <Input
                    value={settings.discount_code || ''}
                    onChange={(e) => setSettings({ ...settings, discount_code: e.target.value })}
                    placeholder="e.g., COMEBACK10"
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty to skip discount offer
                  </p>
                </div>
                
                {settings.discount_code && (
                  <div>
                    <Label>Discount Percentage</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number"
                        min="5"
                        max="50"
                        value={settings.discount_percent}
                        onChange={(e) => setSettings({ ...settings, discount_percent: parseInt(e.target.value) || 10 })}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={savingSettings}>
              {savingSettings && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
